import { useEffect } from 'react';
import { clsx } from 'clsx';
import {
  Inbox as InboxIcon,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowLeft,
  Mail,
  Phone,
  MessageSquare,
} from 'lucide-react';
import { ChannelIcon } from '@/components/modules/ChannelIcon';
import { SentimentIndicator } from '@/components/modules/SentimentIndicator';
import { Tabs } from '@/components/ui/Tabs';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { formatRelative, formatDuration } from '@/lib/format';
import { useInboxStore, type InboxItem } from '@/stores/inboxStore';

const channelTabs = [
  { id: 'all', label: 'All' },
  { id: 'email', label: 'Email' },
  { id: 'sms', label: 'SMS' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'voice', label: 'Voice' },
];

const channelLabels: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  voice: 'Voice',
  live_chat: 'Live Chat',
  messenger: 'Messenger',
  instagram: 'Instagram',
};

function getChannelBadgeVariant(channel: string): 'info' | 'success' | 'purple' | 'default' {
  switch (channel) {
    case 'email':
      return 'info';
    case 'sms':
    case 'whatsapp':
      return 'success';
    case 'voice':
      return 'purple';
    default:
      return 'default';
  }
}

// ---------------------------------------------------------------------------
// Detail views per entity type
// ---------------------------------------------------------------------------

function str(v: unknown): string { return v == null ? '' : String(v); }
function num(v: unknown): number { return typeof v === 'number' ? v : 0; }

function EmailDetail({ item }: { item: InboxItem }) {
  const meta = item.metadata ?? {};
  return (
    <div className="space-y-4">
      {/* Subject */}
      <div>
        <h3 className="text-lg font-semibold text-surface-100">{str(meta.subject) || 'No Subject'}</h3>
        <p className="text-xs text-surface-500 mt-1">Thread ID: {str(meta.threadId) || 'N/A'}</p>
      </div>

      {/* From / To */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-surface-500 mb-0.5">From</p>
          <p className="text-sm text-surface-200">{str(meta.from)}</p>
        </div>
        <div>
          <p className="text-xs text-surface-500 mb-0.5">To</p>
          <p className="text-sm text-surface-200">{str(meta.to)}</p>
        </div>
      </div>

      {/* Classification & sentiment */}
      <div className="flex items-center gap-3">
        {meta.classification ? (
          <Badge>{str(meta.classification)}</Badge>
        ) : null}
        {meta.sentiment ? (
          <SentimentIndicator sentiment={str(meta.sentiment)} showLabel />
        ) : null}
        {meta.isRead === false && (
          <Badge variant="warning">Unread</Badge>
        )}
      </div>

      {/* Body preview */}
      <div className="rounded-lg bg-surface-900 border border-surface-700 p-4">
        <p className="text-sm text-surface-300 whitespace-pre-wrap leading-relaxed">
          {str(meta.bodyPreview)}
        </p>
      </div>
    </div>
  );
}

function MessagingDetail({ item }: { item: InboxItem }) {
  const meta = item.metadata ?? {};
  return (
    <div className="space-y-4">
      {/* Conversation info */}
      <div className="flex items-center gap-3">
        <Badge variant={meta.status === 'active' ? 'success' : meta.status === 'waiting' ? 'warning' : 'default'}>
          {str(meta.status)}
        </Badge>
        <span className="text-xs text-surface-500">
          {num(meta.messageCount)} message{num(meta.messageCount) !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Last message preview */}
      <div className="rounded-lg bg-surface-900 border border-surface-700 p-4">
        {meta.lastSender ? (
          <p className="text-xs text-surface-500 mb-1">{str(meta.lastSender)}</p>
        ) : null}
        <p className="text-sm text-surface-300 leading-relaxed">{item.preview}</p>
      </div>

      <p className="text-xs text-surface-500">
        Open the Messaging Hub to view the full conversation thread.
      </p>
    </div>
  );
}

function VoiceDetail({ item }: { item: InboxItem }) {
  const meta = item.metadata ?? {};
  const duration = meta.duration as number | undefined;
  return (
    <div className="space-y-4">
      {/* Call info */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-surface-500 mb-0.5">From</p>
          <p className="text-sm text-surface-200">{str(meta.phoneFrom)}</p>
        </div>
        <div>
          <p className="text-xs text-surface-500 mb-0.5">To</p>
          <p className="text-sm text-surface-200">{str(meta.phoneTo)}</p>
        </div>
      </div>

      {/* Status, duration, sentiment */}
      <div className="flex items-center gap-3 flex-wrap">
        <Badge variant={meta.status === 'completed' ? 'success' : meta.status === 'failed' ? 'error' : 'warning'}>
          {str(meta.status)}
        </Badge>
        {duration != null && duration > 0 && (
          <span className="text-sm text-surface-300">{formatDuration(duration)}</span>
        )}
        {meta.sentiment ? (
          <SentimentIndicator sentiment={str(meta.sentiment)} showLabel />
        ) : null}
        {meta.escalated ? (
          <Badge variant="error">Escalated</Badge>
        ) : null}
      </div>

      {/* Transcription */}
      {meta.transcription ? (
        <div>
          <p className="text-xs text-surface-500 mb-1">Transcription</p>
          <div className="rounded-lg bg-surface-900 border border-surface-700 p-4">
            <p className="text-sm text-surface-300 whitespace-pre-wrap leading-relaxed">
              {str(meta.transcription)}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ItemDetail({ item }: { item: InboxItem }) {
  return (
    <div className="flex-1 overflow-y-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-surface-700">
        <ChannelIcon channel={item.channel} size="md" />
        <div>
          <p className="text-base font-semibold text-surface-100">
            {item.contactName || 'Unknown Contact'}
          </p>
          <p className="text-xs text-surface-500">
            {channelLabels[item.channel] || item.channel} &middot; {formatRelative(item.timestamp)}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Badge variant={getChannelBadgeVariant(item.channel)}>
            {channelLabels[item.channel] || item.channel}
          </Badge>
          {item.direction === 'inbound' ? (
            <ArrowDownLeft className="h-4 w-4 text-blue-400" />
          ) : (
            <ArrowUpRight className="h-4 w-4 text-emerald-400" />
          )}
        </div>
      </div>

      {/* Content based on entity type */}
      {item.entityType === 'email_message' && <EmailDetail item={item} />}
      {item.entityType === 'messaging_conversation' && <MessagingDetail item={item} />}
      {item.entityType === 'voice_call' && <VoiceDetail item={item} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------
function InboxSkeleton() {
  return (
    <div className="space-y-6 max-w-[1440px]">
      <div>
        <Skeleton className="h-6 w-36 mb-2" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-20 rounded-lg" />
        ))}
      </div>
      <div className="flex gap-0 h-[calc(100vh-260px)] bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
        <div className="w-80 border-r border-surface-700 p-2 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-start gap-3 px-3 py-3">
              <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          ))}
        </div>
        <div className="flex-1 flex flex-col p-6">
          <Skeleton className="h-5 w-48 mb-4" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-3/4 mb-2" />
          <Skeleton className="h-32 w-full rounded-lg mt-4" />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function Inbox() {
  const {
    items,
    selectedId,
    channel,
    isLoading,
    error,
    fetch: fetchInbox,
    select,
    setChannel,
    clearError,
  } = useInboxStore();

  useEffect(() => {
    fetchInbox(channel);
  }, [fetchInbox, channel]);

  const selectedItem = items.find((i) => i.id === selectedId);

  const handleChannelChange = (newChannel: string) => {
    setChannel(newChannel);
    select(null);
    fetchInbox(newChannel);
  };

  if (isLoading && items.length === 0) {
    return <InboxSkeleton />;
  }

  return (
    <div className="space-y-6 max-w-[1440px]">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-surface-100">Inbox</h2>
        <p className="text-sm text-surface-400 mt-0.5">
          Unified communications across email, messaging, and voice
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center justify-between px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={clearError} className="text-xs text-red-400 hover:text-red-300 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Channel tabs */}
      <Tabs tabs={channelTabs} active={channel} onChange={handleChannelChange} />

      {/* Main two-column layout */}
      <div className="flex gap-0 h-[calc(100vh-260px)] bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
        {/* Left: Conversation list — hidden on mobile when item selected */}
        <div className={clsx(
          'w-full md:w-80 border-r border-surface-700 overflow-y-auto flex-shrink-0',
          selectedId ? 'hidden md:block' : 'block',
        )}>
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <InboxIcon className="h-8 w-8 text-surface-600 mb-2" />
              <p className="text-sm text-surface-400">No messages</p>
              <p className="text-xs text-surface-500 mt-1">
                Communications will appear here
              </p>
            </div>
          ) : (
            items.map((item) => (
              <button
                key={item.id}
                onClick={() => select(item.id)}
                className={clsx(
                  'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-surface-700/50 cursor-pointer',
                  selectedId === item.id
                    ? 'bg-surface-700/50'
                    : 'hover:bg-surface-700/30',
                )}
              >
                {/* Channel icon */}
                <div className="flex-shrink-0 mt-0.5">
                  <ChannelIcon channel={item.channel} size="md" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-surface-200 truncate">
                      {item.contactName || 'Unknown'}
                    </span>
                    <span className="text-[10px] text-surface-500 flex-shrink-0">
                      {formatRelative(item.timestamp)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {item.direction === 'inbound' ? (
                      <ArrowDownLeft className="h-3 w-3 text-blue-400 flex-shrink-0" />
                    ) : (
                      <ArrowUpRight className="h-3 w-3 text-emerald-400 flex-shrink-0" />
                    )}
                    <span className="text-xs text-surface-500 truncate">
                      {channelLabels[item.channel] || item.channel}
                    </span>
                  </div>
                  <p className="text-xs text-surface-400 mt-0.5 truncate">{item.preview}</p>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Right: Selected item detail — hidden on mobile when no item selected */}
        <div className={clsx(
          'flex-1 flex flex-col min-w-0',
          selectedId ? 'block' : 'hidden md:flex',
        )}>
          {!selectedItem ? (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState
                icon={<InboxIcon className="h-8 w-8" />}
                title="Select a message"
                description="Choose a conversation from the list to view details"
              />
            </div>
          ) : (
            <>
              {/* Mobile back button */}
              <div className="md:hidden flex items-center gap-2 px-4 py-2.5 border-b border-surface-700">
                <button
                  onClick={() => select(null)}
                  className="p-1.5 -ml-1 rounded-lg text-surface-400 hover:text-surface-200 hover:bg-surface-700 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-medium text-surface-200">Back to inbox</span>
              </div>
              <ItemDetail item={selectedItem} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
